import React from "react";
import { inr, pct } from "@/lib/format";
import { AlertTriangle, CheckCircle2, TrendingDown, TrendingUp, ShieldAlert, PackageX } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function FlagCard({ icon: Icon, title, triggered, value, threshold, description, testid }) {
  const cls = triggered ? "text-[hsl(var(--negative))]" : "text-[hsl(var(--positive))]";
  return (
    <div className="p-6 min-h-[160px] flex flex-col justify-between" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline text-muted-foreground">{title}</div>
          <div className={`mt-2 font-mono text-2xl font-semibold ${cls}`}>{value}</div>
          <div className="text-[10px] text-muted-foreground font-mono mt-1">Threshold {threshold}</div>
        </div>
        <div className={`p-2 border border-border ${triggered ? "bg-[hsl(var(--negative))]/10" : ""}`}>
          <Icon className={`h-5 w-5 ${cls}`} />
        </div>
      </div>
      <div className={`text-xs mt-3 ${triggered ? "text-[hsl(var(--negative))]" : "text-muted-foreground"}`}>
        {triggered ? description.triggered : description.ok}
      </div>
    </div>
  );
}

function SkuMiniRow({ s, tone }) {
  const cls = tone === "positive" ? "text-[hsl(var(--positive))]" : "text-[hsl(var(--negative))]";
  return (
    <TableRow>
      <TableCell className="font-mono text-xs">{s.sku_code}</TableCell>
      <TableCell className="max-w-[220px] truncate">{s.product_name}</TableCell>
      <TableCell className="text-right font-mono">{inr(s.mrp, { decimals: 0 })}</TableCell>
      <TableCell className={`text-right font-mono font-semibold ${cls}`}>{inr(s.unit_net_profit, { decimals: 2 })}</TableCell>
      <TableCell className={`text-right font-mono ${cls}`}>{pct(s.unit_margin_pct, 1)}</TableCell>
    </TableRow>
  );
}

export default function AuditPanel({ audit }) {
  if (!audit) return <div className="p-12 text-muted-foreground">Loading audit…</div>;
  const { flags, top_drivers, top_losers, sku_analysis, action_items, allocation } = audit;

  return (
    <div className="space-y-6" data-testid="audit-panel">
      {/* Flag cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 grid-skeleton border border-border">
        <FlagCard
          icon={TrendingDown}
          title="Bleeding SKUs"
          triggered={flags.bleeding_skus.triggered}
          value={`${flags.bleeding_skus.count} SKU${flags.bleeding_skus.count === 1 ? "" : "s"}`}
          threshold="Unit net profit ≥ 0"
          description={{
            triggered: `${flags.bleeding_skus.count} product${flags.bleeding_skus.count === 1 ? "" : "s"} losing money on every unit sold after fully-loaded costs.`,
            ok: "Every SKU is profitable on a fully-loaded per-unit basis.",
          }}
          testid="flag-bleeding"
        />
        <FlagCard
          icon={ShieldAlert}
          title="Discount Risk"
          triggered={flags.discount_risk.triggered}
          value={pct(flags.discount_risk.value, 1)}
          threshold={`≤ ${flags.discount_risk.threshold}% of gross`}
          description={{
            triggered: "Discount depth is eroding margin faster than volume gains can justify.",
            ok: "Coupon usage is within a healthy contribution-margin envelope.",
          }}
          testid="flag-discount"
        />
        <FlagCard
          icon={AlertTriangle}
          title="ROAS Efficiency"
          triggered={flags.roas_efficiency.triggered}
          value={`${flags.roas_efficiency.value.toFixed(2)}x`}
          threshold={`≥ ${flags.roas_efficiency.threshold}x blended`}
          description={{
            triggered: "Blended ROAS below breakeven zone. Trim inefficient ad sets.",
            ok: "Ad efficiency comfortably above breakeven.",
          }}
          testid="flag-roas"
        />
      </div>

      {/* High-RTO flag */}
      {flags.high_rto && (
        <div className="border border-border" data-testid="high-rto-panel">
          <div className={`p-6 flex items-start justify-between gap-4 ${flags.high_rto.triggered ? "border-l-2 border-l-[hsl(var(--negative))]" : ""}`}>
            <div className="flex items-start gap-4">
              <div className={`p-2 border border-border ${flags.high_rto.triggered ? "bg-[hsl(var(--negative))]/10" : ""}`}>
                <PackageX className={`h-5 w-5 ${flags.high_rto.triggered ? "text-[hsl(var(--negative))]" : "text-[hsl(var(--positive))]"}`} />
              </div>
              <div>
                <div className="overline text-muted-foreground">Return & RTO Rate</div>
                <div className={`font-mono text-2xl font-semibold mt-1 ${flags.high_rto.triggered ? "text-[hsl(var(--negative))]" : "text-[hsl(var(--positive))]"}`} data-testid="high-rto-value">
                  {pct(flags.high_rto.value, 2)}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono mt-1">Threshold ≤ {flags.high_rto.threshold}% per SKU / Channel</div>
              </div>
            </div>
            <div className="text-xs max-w-md">
              {flags.high_rto.triggered
                ? "Return rates above 12% on one or more channels/SKUs. Review COD gating, address validation, product listing quality, and courier reliability."
                : "Return rates comfortably below the 12% threshold across channels and SKUs."}
            </div>
          </div>

          {(flags.high_rto.channels?.length || flags.high_rto.skus?.length) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 border-t border-border">
              <div className="border-r border-border" data-testid="high-rto-channels">
                <div className="p-3 border-b border-border overline text-muted-foreground">Flagged channels</div>
                {flags.high_rto.channels?.length === 0 && <div className="p-4 text-xs text-muted-foreground">None</div>}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="overline">Channel</TableHead>
                      <TableHead className="overline text-right">Orders</TableHead>
                      <TableHead className="overline text-right">Returns</TableHead>
                      <TableHead className="overline text-right">Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flags.high_rto.channels?.map((c) => (
                      <TableRow key={c.channel} data-testid={`rto-channel-${c.channel}`}>
                        <TableCell><span className="uppercase font-mono text-[10px] px-1.5 py-0.5 border border-border">{c.channel}</span></TableCell>
                        <TableCell className="text-right font-mono">{c.orders}</TableCell>
                        <TableCell className="text-right font-mono">{c.returns}</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-[hsl(var(--negative))]">{pct(c.rate_pct, 1)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div data-testid="high-rto-skus">
                <div className="p-3 border-b border-border overline text-muted-foreground">Flagged SKUs</div>
                {flags.high_rto.skus?.length === 0 && <div className="p-4 text-xs text-muted-foreground">None</div>}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="overline">SKU</TableHead>
                      <TableHead className="overline text-right">Units</TableHead>
                      <TableHead className="overline text-right">Returned</TableHead>
                      <TableHead className="overline text-right">Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flags.high_rto.skus?.map((s) => (
                      <TableRow key={s.sku_code} data-testid={`rto-sku-${s.sku_code}`}>
                        <TableCell className="font-mono text-xs">{s.sku_code}</TableCell>
                        <TableCell className="text-right font-mono">{s.units}</TableCell>
                        <TableCell className="text-right font-mono">{s.returned_units}</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-[hsl(var(--negative))]">{pct(s.rate_pct, 1)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Action items */}
      <div className="border border-border p-6" data-testid="audit-action-items">
        <div className="overline text-muted-foreground mb-3">Key Action Items</div>
        <ul className="space-y-2">
          {action_items.map((a, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              {audit.flags.bleeding_skus.triggered || audit.flags.discount_risk.triggered || audit.flags.roas_efficiency.triggered
                ? <AlertTriangle className="h-4 w-4 text-[hsl(var(--negative))] shrink-0 mt-0.5" />
                : <CheckCircle2 className="h-4 w-4 text-[hsl(var(--positive))] shrink-0 mt-0.5" />}
              <span>{a}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Drivers vs Losers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border" data-testid="top-drivers">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[hsl(var(--positive))]" />
            <div>
              <div className="overline text-muted-foreground">Ranked by unit net profit</div>
              <div className="font-display text-base font-semibold">Top 3 Profit Drivers</div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="overline">SKU</TableHead>
                <TableHead className="overline">Product</TableHead>
                <TableHead className="overline text-right">MRP</TableHead>
                <TableHead className="overline text-right">Unit Net</TableHead>
                <TableHead className="overline text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top_drivers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No SKUs</TableCell></TableRow>}
              {top_drivers.map((s) => <SkuMiniRow key={s.sku_id} s={s} tone="positive" />)}
            </TableBody>
          </Table>
        </div>

        <div className="border border-border" data-testid="top-losers">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-[hsl(var(--negative))]" />
            <div>
              <div className="overline text-muted-foreground">Ranked by unit net profit</div>
              <div className="font-display text-base font-semibold">Top 3 Loss Makers</div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="overline">SKU</TableHead>
                <TableHead className="overline">Product</TableHead>
                <TableHead className="overline text-right">MRP</TableHead>
                <TableHead className="overline text-right">Unit Net</TableHead>
                <TableHead className="overline text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top_losers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No SKUs</TableCell></TableRow>}
              {top_losers.map((s) => <SkuMiniRow key={s.sku_id} s={s} tone={s.unit_net_profit >= 0 ? "positive" : "negative"} />)}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Full SKU table */}
      <div className="border border-border" data-testid="sku-audit-table">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="overline text-muted-foreground">Fully-loaded per-unit economics</div>
            <div className="font-display text-base font-semibold">SKU Profitability Ledger</div>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground text-right">
            <div>Ad alloc / unit · {inr(allocation.avg_ad_per_unit, { decimals: 2 })}</div>
            <div>Ship alloc / unit · {inr(allocation.avg_ship_per_unit, { decimals: 2 })}</div>
            <div>PG · {pct(allocation.avg_pg_fee_pct, 2)}</div>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="overline">SKU</TableHead>
              <TableHead className="overline">Product</TableHead>
              <TableHead className="overline text-right">MRP</TableHead>
              <TableHead className="overline text-right">Unit Cost</TableHead>
              <TableHead className="overline text-right">PG</TableHead>
              <TableHead className="overline text-right">Ad</TableHead>
              <TableHead className="overline text-right">Ship</TableHead>
              <TableHead className="overline text-right">Unit Net</TableHead>
              <TableHead className="overline text-right">Margin</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sku_analysis.map((s) => {
              const insufficient = s.insufficient_data;
              const cls = s.unit_net_profit >= 0 ? "text-[hsl(var(--positive))]" : "text-[hsl(var(--negative))]";
              return (
                <TableRow key={s.sku_id} data-testid={`audit-row-${s.sku_code}`}>
                  <TableCell className="font-mono text-xs">{s.sku_code}</TableCell>
                  <TableCell>
                    <div>{s.product_name}</div>
                    {insufficient && (
                      <div className="text-[10px] font-mono text-muted-foreground mt-0.5" data-testid={`insufficient-${s.sku_code}`}>
                        {s.ad_source === "category_cac" ? `Insufficient volume · using ${s.category} category CAC` : "Insufficient Data"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">{inr(s.mrp, { decimals: 0 })}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.unit_cost, { decimals: 2 })}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.unit_pg_alloc, { decimals: 2 })}</TableCell>
                  <TableCell className="text-right font-mono">
                    {insufficient && s.ad_source === "insufficient_data" ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className={s.ad_cap_applied ? "underline decoration-dotted" : ""} title={s.ad_cap_applied ? "Capped at MRP (ex-GST)" : ""}>{inr(s.unit_ad_alloc, { decimals: 2 })}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">{inr(s.unit_ship_alloc, { decimals: 2 })}</TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${cls}`}>{inr(s.unit_net_profit, { decimals: 2 })}</TableCell>
                  <TableCell className={`text-right font-mono ${cls}`}>{pct(s.unit_margin_pct, 1)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
